import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, HelpCircle, RotateCcw, Loader2, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useProgress } from '../../context/ProgressContext';
import { cn } from '../../lib/utils';

/**
 * Quiz: server-authoritative single-choice quiz.
 * The question + options come from the backend; the correct answer is NEVER sent to the client.
 * On submit, we POST the selected index to /api/user/quiz-attempt and the server returns pass/fail,
 * the correct index (so we can show feedback), and an explanation.
 * The server also updates completedModules in the DB — the client never writes those values.
 */
export function Quiz({ moduleId, question, options, requiresAuth }) {
  const { authFetch, isAuthenticated } = useAuth();
  const { progress, refreshFromServer } = useProgress();

  const savedResult = progress?.quizResults?.[String(moduleId)] || progress?.quizResults?.[moduleId];

  const [selectedAnswer, setSelectedAnswer] = useState(
    typeof savedResult?.selectedAnswer === 'number' ? savedResult.selectedAnswer : null
  );
  const [submitted, setSubmitted] = useState(!!savedResult?.passed);
  const [passed, setPassed] = useState(!!savedResult?.passed);
  const [correctIndex, setCorrectIndex] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Re-sync when moduleId changes (navigating between modules)
  useEffect(() => {
    const r = progress?.quizResults?.[String(moduleId)] || progress?.quizResults?.[moduleId];
    if (r?.passed) {
      setSelectedAnswer(typeof r.selectedAnswer === 'number' ? r.selectedAnswer : null);
      setSubmitted(true);
      setPassed(true);
    } else {
      setSelectedAnswer(null);
      setSubmitted(false);
      setPassed(false);
      setCorrectIndex(null);
      setExplanation('');
    }
    setError(null);
  }, [moduleId, progress?.quizResults]);

  const handleSubmit = useCallback(async () => {
    if (selectedAnswer === null) return;
    if (!isAuthenticated) {
      setError('Please sign in or create a free account to record your quiz result.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await authFetch('/api/user/quiz-attempt', {
        method: 'POST',
        body: JSON.stringify({ module_id: moduleId, selected_index: selectedAnswer }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data?.detail || 'Could not submit your answer.');
        return;
      }
      setPassed(data.passed);
      setCorrectIndex(data.correct_index);
      setExplanation(data.explanation || '');
      setSubmitted(true);
      // Refresh progress from server so completed modules + quiz results are in sync
      if (refreshFromServer) refreshFromServer();
    } catch (e) {
      setError('Could not submit your answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [selectedAnswer, moduleId, authFetch, isAuthenticated, refreshFromServer]);

  const handleRetry = () => {
    setSubmitted(false);
    setSelectedAnswer(null);
    setPassed(false);
    setCorrectIndex(null);
    setExplanation('');
    setError(null);
  };

  const showResult = submitted;

  return (
    <div className="bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8" data-testid={`quiz-${moduleId}`}>
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-info/10 flex items-center justify-center flex-shrink-0">
          <HelpCircle className="w-6 h-6 text-info" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-xl text-text mb-1">Quick Quiz</h3>
          <p className="text-text-muted text-sm">Test your understanding — pass to mark this module complete.</p>
        </div>
      </div>

      <p className="font-heading font-semibold text-lg text-text mb-6">{question}</p>

      <div className="space-y-3 mb-6">
        {options.map((option, index) => {
          const isSelected = selectedAnswer === index;
          const isCorrectOption = showResult && correctIndex === index;
          const isWrongSelection = showResult && isSelected && !passed;

          let optionClass = 'quiz-option';
          if (showResult) {
            if (isCorrectOption) optionClass += ' correct';
            else if (isWrongSelection) optionClass += ' incorrect';
          } else if (isSelected) {
            optionClass += ' selected';
          }

          return (
            <button
              key={index}
              data-testid={`quiz-option-${index}`}
              onClick={() => !showResult && !submitting && setSelectedAnswer(index)}
              disabled={showResult || submitting}
              className={cn(
                'w-full text-left p-4 rounded-xl border-2 transition-all',
                optionClass,
                !showResult && !submitting && 'hover:border-primary cursor-pointer',
                (showResult || submitting) && 'cursor-default'
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
                  isCorrectOption && 'bg-success text-white',
                  isWrongSelection && 'bg-error text-white',
                  !showResult && isSelected && 'bg-primary text-white',
                  !showResult && !isSelected && 'bg-stone-100 text-text-muted'
                )}>
                  {isCorrectOption ? <CheckCircle className="w-5 h-5" /> :
                   isWrongSelection ? <XCircle className="w-5 h-5" /> :
                   String.fromCharCode(65 + index)}
                </div>
                <span className="font-medium text-text">{option}</span>
              </div>
            </button>
          );
        })}
      </div>

      {showResult && (
        <div
          className={cn(
            'rounded-xl p-4 mb-6',
            passed ? 'bg-success/10 border border-success/30' : 'bg-error/10 border border-error/30'
          )}
          data-testid="quiz-result"
        >
          <div className="flex items-start gap-3">
            {passed ? (
              <CheckCircle className="w-6 h-6 text-success flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-6 h-6 text-error flex-shrink-0 mt-0.5" />
            )}
            <div>
              <p className={cn('font-bold mb-1', passed ? 'text-success' : 'text-error')}>
                {passed ? 'Correct! Module marked as complete.' : 'Not quite right'}
              </p>
              {explanation && <p className="text-text-muted text-sm">{explanation}</p>}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-xl p-4 mb-4 bg-error/10 border border-error/30 text-error text-sm" data-testid="quiz-error">
          {error}
        </div>
      )}

      {requiresAuth && !showResult && (
        <div className="rounded-xl p-4 mb-4 bg-stone-50 border border-stone-200 text-text-muted text-sm flex items-center gap-2">
          <Lock className="w-4 h-4" />
          <span>Create a free account to record your quiz result and track completion.</span>
        </div>
      )}

      <div className="flex gap-3">
        {!showResult ? (
          <Button
            data-testid="quiz-submit"
            onClick={handleSubmit}
            disabled={selectedAnswer === null || submitting || requiresAuth}
            className="bg-primary text-white hover:bg-primary-600 rounded-full px-8 py-6 text-lg font-bold"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Check Answer
          </Button>
        ) : !passed && (
          <Button
            data-testid="quiz-retry"
            onClick={handleRetry}
            variant="outline"
            className="rounded-full px-6 py-4 font-semibold"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        )}
      </div>
    </div>
  );
}
